import React from 'react';
import classNames from 'classnames';

import { ExternalLink } from '../../../../components';

import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';

import css from './SectionFooter.module.css';
import logo from '../../../../assets/gearly-logo-mobile-3.png';

const partitionFooterBlocks = blocks => {
  const list = blocks || [];
  const footerBlocks = list.filter(b => b.blockType === 'footerBlock');
  const socialBlocks = list.filter(b => b.blockType === 'socialMediaLink');
  const otherBlocks = list.filter(
    b => b.blockType !== 'footerBlock' && b.blockType !== 'socialMediaLink'
  );
  return { footerBlocks, socialBlocks, otherBlocks };
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Footer: column 1 logo, columns 2–3 footer link lists (footerBlock), column 4 social icons.
 * CMS order: first two `footerBlock` entries map to menus; all `socialMediaLink` blocks group in the last column.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns legacy; layout is fixed to four columns
 * @param {Array?} props.socialMediaLinks legacy; prefer social blocks in `blocks`
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks
 * @param {Object} props.options
 * @param {Object?} props.linkLogoToExternalSite
 * @returns {JSX.Element}
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    socialMediaLinks = [],
    appearance,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;
  const MAX_MOBILE_SCREEN_WIDTH = 1024;


  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  const { footerBlocks, socialBlocks, otherBlocks } = partitionFooterBlocks(blocks);

  if (otherBlocks.length > 0 && typeof console !== 'undefined' && console.warn) {
    console.warn(
      `[SectionFooter ${sectionId}] Ignoring ${otherBlocks.length} non-footer/social block(s). Use footerBlock or socialMediaLink.`
    );
  }

  const [menuLeft, menuRight] = footerBlocks;
  const blockBuilderProps = { sectionId, options };

  const logoImg = (
    <img src={logo} alt="" className={css.logoImg} width={160} height={48} />
  );

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={css.columns}>
          <div className={css.colLogo}>
            {linkLogoToExternalSite?.href ? (
              <ExternalLink
                className={css.logoLink}
                href={linkLogoToExternalSite.href}
                target="_self"
              >
                {logoImg}
              </ExternalLink>
            ) : (
              logoImg
            )}
          </div>

          <div className={classNames(css.colMenu, css.colMenuPrimary)}>
            {menuLeft ? (
              <BlockBuilder {...blockBuilderProps} blocks={[menuLeft]} />
            ) : null}
          </div>

          <div className={classNames(css.colMenu, css.colMenuSecondary)}>
            {menuRight ? (
              <BlockBuilder {...blockBuilderProps} blocks={[menuRight]} />
            ) : null}
          </div>

          <div className={css.colSocial}>
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
